import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";

import {
  adjudicarLeilao,
  cancelarLeilao,
  convidarParaCarteira,
  encerrarLeilao,
  obterLeilao,
  reabrirLeilao,
  reenviarConvite,
} from "services/leilaoFreteApi";
import obterUserNome from "utils/userNome";
import { fmtLimite } from "utils/limitesColetaEntrega"; // PO1-3481
import {
  CONVIDADO_UI,
  ESTADO_UI,
  JUSTIFICATIVA_MIN,
  ROTULO_REDE,
  TITULO_SELO_REDE,
  TONE_VAR,
  cidadeUf,
  countdown,
  diferencaPct,
  ehCandidatoRede,
  fmtBRL,
  fmtData,
  fmtDataHora,
  fmtDuracao,
  fmtHora,
  fmtNumero,
  fmtPct,
  formatBRLInput,
  justificativaValida,
  offsetDoServidor,
  ordenarVigentes,
  parseBRL,
  precisaJustificar,
  progressoJanela,
  resumoConvidados,
} from "./regras";

// Acompanhar a oferta (H3 PO1-3084) e escolher o vencedor (H4 PO1-3086).
// Polling de 10 s só com a aba visível e só enquanto a oferta está aberta
// (molde components/autopilot/FreightIndicatorsCard.js). O countdown usa a
// hora do servidor (serverNow da resposta), não a do navegador.

const POLL_MS = 10000;

function Badge({ tone, children, title }) {
  return (
    <span className={`lf-badge lf-badge-${tone || "neutral"}`} title={title}>
      {children}
    </span>
  );
}

export default function LeilaoDetalheModal({ isOpen, onClose, empresaId, leilaoId, onChanged }) {
  const history = useHistory();
  const [leilao, setLeilao] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [acaoErro, setAcaoErro] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // 'cancelar' | 'encerrar'
  const [lanceEscolhido, setLanceEscolhido] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [novoAlvo, setNovoAlvo] = useState("");
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [convidando, setConvidando] = useState(false);
  const fetchRef = useRef(null);

  const carregar = useCallback(
    async (silencioso) => {
      if (!empresaId || !leilaoId) return;
      if (!silencioso) setLoading(true);
      try {
        const r = await obterLeilao(empresaId, leilaoId);
        setLeilao(r);
        setOffset(offsetDoServidor(r.serverNow));
        setErro(null);
      } catch (err) {
        const data = (err.response && err.response.data) || {};
        setErro(data.error || "Não foi possível carregar o leilão.");
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [empresaId, leilaoId],
  );

  useEffect(() => {
    fetchRef.current = carregar;
  }, [carregar]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setLeilao(null);
    setErro(null);
    setAcaoErro(null);
    setConfirmar(null);
    setLanceEscolhido(null);
    setJustificativa("");
    setNovoAlvo("");
    setMostrarHistorico(false);
    carregar(false);
    return undefined;
  }, [isOpen, carregar]);

  // Polling (aberta) + tick do countdown a cada segundo.
  const estado = leilao && leilao.estado;
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || estado !== "aberta") return undefined;
    const p = setInterval(() => {
      if (document.visibilityState === "visible" && fetchRef.current) fetchRef.current(true);
    }, POLL_MS);
    return () => clearInterval(p);
  }, [isOpen, estado]);

  const vigentes = useMemo(() => ordenarVigentes(leilao && leilao.lances), [leilao]);
  const menorId = leilao && leilao.menorLanceId;
  const resumo = useMemo(() => resumoConvidados(leilao), [leilao]);
  const cd = leilao ? countdown(leilao.fechaEm, offset) : null;

  useEffect(() => {
    if (leilao && leilao.estado === "encerrada" && !lanceEscolhido && menorId) setLanceEscolhido(menorId);
    if (leilao && leilao.estado === "deserta" && !novoAlvo) setNovoAlvo(formatBRLInput(Number(leilao.precoAlvo)));
  }, [leilao, lanceEscolhido, menorId, novoAlvo]);

  const tratarErro = (err, fallback) => {
    const data = (err.response && err.response.data) || {};
    const detalhe = data.errors ? Object.keys(data.errors).map((k) => data.errors[k]).join(" ") : "";
    setAcaoErro(`${data.error || fallback}${detalhe ? ` ${detalhe}` : ""}`);
  };

  const executar = async (fn, fallback) => {
    if (busy) return;
    setBusy(true);
    setAcaoErro(null);
    try {
      const r = await fn();
      if (r && r.estado) {
        setLeilao(r);
        if (r.serverNow) setOffset(offsetDoServidor(r.serverNow));
      } else {
        await carregar(true);
      }
      setConfirmar(null);
      if (onChanged) onChanged(r);
      return r;
    } catch (err) {
      tratarErro(err, fallback);
      await carregar(true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleEncerrar = () => executar(() => encerrarLeilao(empresaId, leilaoId), "Não foi possível encerrar.");
  const handleCancelar = () => executar(() => cancelarLeilao(empresaId, leilaoId, { userNome: obterUserNome() }), "Não foi possível cancelar.");
  const handleReenviar = (convidadoId) => executar(() => reenviarConvite(empresaId, leilaoId, convidadoId), "Não foi possível reenviar o convite.");
  const foraDoMenor = precisaJustificar(leilao, lanceEscolhido);
  const handleAdjudicar = () => {
    if (!lanceEscolhido) return;
    if (foraDoMenor && !justificativaValida(justificativa)) {
      setAcaoErro(`Escolher fora do menor lance exige justificativa com ${JUSTIFICATIVA_MIN} caracteres ou mais.`);
      return;
    }
    executar(
      () => adjudicarLeilao(empresaId, leilaoId, { lanceId: lanceEscolhido, justificativa: foraDoMenor ? justificativa.trim() : undefined, userNome: obterUserNome() }),
      "Não foi possível escolher o vencedor.",
    );
  };
  const handleReabrir = async () => {
    const alvo = parseBRL(novoAlvo);
    if (!(alvo > 0)) {
      setAcaoErro("Informe o novo preço-alvo.");
      return;
    }
    const r = await executar(() => reabrirLeilao(empresaId, leilaoId, { precoAlvo: alvo, userNome: obterUserNome() }), "Não foi possível reabrir.");
    if (r && r._id && onChanged) onChanged(r, { reaberto: true });
  };
  const handleConvidarCarteira = async () => {
    if (!leilao || !leilao.vencedor || convidando) return;
    const conv = (leilao.convidados || []).find((c) => c.idTransportadora === leilao.vencedor.idTransportadora);
    setConvidando(true);
    setAcaoErro(null);
    try {
      await convidarParaCarteira(empresaId, { cnpj: conv && conv.cnpj, nome: leilao.vencedor.nome });
      setAcaoErro(null);
      setLeilao((cur) => (cur ? { ...cur, vencedorNaCarteira: true, convitePedido: true } : cur));
    } catch (err) {
      tratarErro(err, "Não foi possível convidar para a carteira.");
    } finally {
      setConvidando(false);
    }
  };

  const nomeDo = (idTransportadora) => {
    const c = (leilao.convidados || []).find((x) => x.idTransportadora === idTransportadora);
    return c ? c.nome : `Transportadora ${idTransportadora}`;
  };

  const ui = leilao ? ESTADO_UI[leilao.estado] || ESTADO_UI.cancelada : null;

  const tom = ui ? TONE_VAR[ui.tone] || TONE_VAR.neutral : TONE_VAR.neutral;
  const aberta = Boolean(leilao && leilao.estado === "aberta");
  const encerrada = Boolean(leilao && leilao.estado === "encerrada");
  const progresso = leilao && aberta ? progressoJanela(leilao.abertaEm, leilao.fechaEm, offset) : 1;
  const menor = vigentes.length ? vigentes[0] : null;
  const difAlvo = menor && leilao ? diferencaPct(menor.valor, leilao.precoAlvo) : null;
  const carga = (leilao && leilao.carga) || {};

  let banner = null;
  if (leilao) {
    if (aberta) banner = `Recebendo lances até ${fmtHora(leilao.fechaEm)}. A janela fecha sozinha; você pode encerrar antes.`;
    else if (encerrada) banner = `Janela encerrada às ${fmtHora(leilao.encerradaEm || leilao.fechaEm)}. Escolha a vencedora entre os lances abaixo.`;
    else if (leilao.estado === "deserta") banner = `Ninguém deu lance até ${fmtHora(leilao.fechaEm)}. Reabra com um novo preço-alvo ou siga com a cotação normal.`;
    else if (leilao.estado === "cancelada") banner = `Cancelado em ${fmtDataHora(leilao.canceladaEm)}${leilao.canceladaPor && leilao.canceladaPor.nome ? ` por ${leilao.canceladaPor.nome}` : ""}.`;
    else if (leilao.estado === "adjudicada" && leilao.vencedor)
      banner = leilao.vencedor.aprovacaoPendente
        ? `${leilao.vencedor.nome} escolhida. A cotação aguarda a aprovação de alçada (valor acima do orçado da versão anterior).`
        : `${leilao.vencedor.nome} escolhida. A cotação passou a Aprovado com a vencedora e os lances gravados.`;
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top" contentClassName="lf-modal" backdrop="static" fade={false}>
      <div className="lf-head">
        <span className="lf-head-title">
          <span>{leilao ? `Leilão nº ${leilao.numero}` : "Leilão"}</span>
          {leilao ? <Badge tone={ui.tone}>{ui.label}</Badge> : null}
          {leilao ? (
            <span className="lc-badge lc-badge-cotacao" title="Cotação de origem">
              Cotação {leilao.numeroCotacao != null ? `nº ${leilao.numeroCotacao}` : leilao.baseNumEmbarque}
            </span>
          ) : null}
          {leilao && leilao.reabertaDe ? <Badge tone="neutral">Reaberto</Badge> : null}
        </span>
        <button type="button" className="lf-head-close" onClick={onClose} aria-label="Fechar">
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {loading && !leilao ? (
          <div className="lf-empty-state">
            <Spinner size="sm" /> Carregando...
          </div>
        ) : erro && !leilao ? (
          <div className="lf-aviso lf-aviso-danger">{erro}</div>
        ) : leilao ? (
          <>
            <div className="lf-banner" style={{ borderLeftColor: tom, color: tom, background: `color-mix(in srgb, ${tom} 10%, transparent)` }}>
              <strong>{ui.label}</strong>
              <span className="lf-banner-sep">—</span>
              <span>{banner}</span>
            </div>

            <div className="lf-kpis">
              <div className="lf-kpi">
                <div className="lf-kpi-label">{aberta ? "Tempo restante" : "Janela"}</div>
                {aberta && cd ? (
                  <>
                    <div className={`lf-kpi-value lf-kpi-countdown lf-text-${cd.level}`}>{cd.texto}</div>
                    <div className="lf-progress" title={`${Math.round(progresso * 100)}% da janela`}>
                      <div className={`lf-progress-bar lf-bg-${cd.level}`} style={{ width: `${Math.round(progresso * 100)}%` }} />
                    </div>
                    <div className="lf-kpi-sub">
                      fecha às {fmtHora(leilao.fechaEm)} · {fmtDuracao(leilao.duracaoMin)}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="lf-kpi-value">{fmtDuracao(leilao.duracaoMin)}</div>
                    <div className="lf-kpi-sub">
                      {fmtHora(leilao.abertaEm)} → {fmtHora(leilao.encerradaEm || leilao.canceladaEm || leilao.fechaEm)}
                      {leilao.encerradaPor === "embarcador" ? " · encerrada antes" : ""}
                    </div>
                  </>
                )}
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Preço-alvo</div>
                <div className="lf-kpi-value">{fmtBRL(leilao.precoAlvo)}</div>
                <div className="lf-kpi-sub">visível para as convidadas</div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Menor lance</div>
                <div className="lf-kpi-value">{menor ? fmtBRL(menor.valor) : "—"}</div>
                <div className="lf-kpi-sub">
                  {menor ? (
                    <>
                      {menor.nome || nomeDo(menor.idTransportadora)}
                      {difAlvo !== null ? (
                        <span className={difAlvo <= 0 ? "lf-text-success" : "lf-text-warning"}>
                          {" "}
                          · {difAlvo <= 0 ? `${fmtPct(Math.abs(difAlvo))} abaixo do alvo` : `${fmtPct(difAlvo)} acima do alvo`}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "nenhum lance ainda"
                  )}
                </div>
              </div>
              <div className="lf-kpi">
                <div className="lf-kpi-label">Respostas</div>
                <div className="lf-kpi-value">
                  {resumo.responderam}
                  <span className="lf-kpi-de"> de {resumo.total}</span>
                </div>
                <div className="lf-kpi-sub">
                  {resumo.abriram} abriram · {resumo.recusaram} recusaram
                </div>
              </div>
            </div>

            <div className="lf-section">
              <div className="lf-carga">
                <div className="lf-rota">
                  {cidadeUf(carga.origem)} <span className="lf-rota-seta">→</span> {cidadeUf(carga.destino)}
                </div>
                <div className="lf-chips">
                  {carga.urgente ? <span className="lf-chip lf-chip-urgente">Urgente</span> : null}
                  {carga.coletarAte ? (
                    <span className="lf-chip">Coletar até {fmtLimite(carga.coletarAte)}</span>
                  ) : (
                    <span className="lf-chip">Coleta {fmtData(carga.dataColeta)}</span>
                  )}
                  {carga.entregarAte ? <span className="lf-chip">Entregar até {fmtLimite(carga.entregarAte)}</span> : null}
                  {carga.pesoBruto ? <span className="lf-chip">{fmtNumero(carga.pesoBruto)} kg</span> : null}
                  {carga.volume ? <span className="lf-chip">{fmtNumero(carga.volume, 3)} m³</span> : null}
                  <span className="lf-chip">NF {fmtBRL(carga.valorNF)}</span>
                  {carga.tipoVeiculo ? <span className="lf-chip">{carga.tipoVeiculo}</span> : null}
                </div>
              </div>
              {carga.observacoes ? (
                <div className="lf-observacoes">
                  <div className="lf-observacoes-titulo">Observações enviadas às transportadoras</div>
                  <div className="lf-observacoes-texto">{carga.observacoes}</div>
                </div>
              ) : null}
              <div className="lf-muted" style={{ marginTop: 8 }}>
                Publicado em {fmtDataHora(leilao.abertaEm)}
                {leilao.criadoPor && leilao.criadoPor.nome ? ` por ${leilao.criadoPor.nome}` : ""} ·{" "}
                <button type="button" className="lf-link" onClick={() => history.push(`/app/gobid/operacao/listagem-cotacoes?abrir=${leilao.embarqueId}`)}>
                  abrir a cotação
                </button>
              </div>
            </div>

            {leilao.estado === "adjudicada" && leilao.vencedor ? (
              <div className="lf-section">
                <div className="lf-section-title">Vencedora</div>
                <div className="lf-row is-selected">
                  <div className="lf-row-main">
                    <div className="lf-row-nome">{leilao.vencedor.nome}</div>
                    <div className="lf-muted">
                      escolhida em {fmtDataHora(leilao.vencedor.escolhidoEm)}
                      {leilao.vencedor.escolhidoPor && leilao.vencedor.escolhidoPor.nome ? ` por ${leilao.vencedor.escolhidoPor.nome}` : ""}
                    </div>
                    {leilao.vencedor.foraDoMenor ? <div className="lf-muted">Fora do menor lance. Justificativa: {leilao.vencedor.justificativa}</div> : null}
                  </div>
                  <div className="lf-row-side">
                    <span className="lf-row-valor">{fmtBRL(leilao.vencedor.valor)}</span>
                    <span className="lf-row-prazo">{leilao.vencedor.prazoDias} d</span>
                  </div>
                </div>
                {leilao.vencedorNaCarteira === false && !leilao.convitePedido ? (
                  <div className="lf-inline-acao">
                    <span>A vencedora está fora da sua carteira.</span>
                    <Button size="sm" color="info" outline onClick={handleConvidarCarteira} disabled={convidando}>
                      {convidando ? "Convidando..." : "Convidar para a carteira"}
                    </Button>
                  </div>
                ) : null}
                {leilao.convitePedido ? <div className="lf-muted" style={{ marginTop: 8 }}>Convite para a carteira enviado.</div> : null}
              </div>
            ) : null}

            <div className="lf-section">
              <div className="lf-section-head">
                <div className="lf-section-title">
                  Lances {vigentes.length ? `(${vigentes.length})` : ""}
                </div>
                {leilao.lances && leilao.lances.length > vigentes.length ? (
                  <button type="button" className="lf-link" onClick={() => setMostrarHistorico((v) => !v)}>
                    {mostrarHistorico ? "ocultar histórico" : `histórico completo (${leilao.lances.length})`}
                  </button>
                ) : null}
              </div>
              {vigentes.length === 0 ? (
                <div className="lf-vazio">{aberta ? "Nenhum lance ainda. A lista atualiza sozinha a cada 10 segundos." : "Nenhum lance recebido."}</div>
              ) : (
                <div className="lf-rows">
                  {vigentes.map((l, i) => {
                    const ehMenor = String(l._id) === String(menorId);
                    const escolhido = encerrada && String(l._id) === String(lanceEscolhido);
                    const Tag = encerrada ? "button" : "div";
                    return (
                      <Tag
                        key={l._id}
                        type={encerrada ? "button" : undefined}
                        className={`lf-row${escolhido ? " is-selected" : ""}${encerrada ? " is-clicavel" : ""}`}
                        onClick={encerrada ? () => setLanceEscolhido(String(l._id)) : undefined}
                      >
                        <div className="lf-row-main">
                          <div className="lf-row-nome">
                            <span className="lf-row-rank">{i + 1}º</span>
                            {l.nome || nomeDo(l.idTransportadora)}
                            {ehMenor ? <Badge tone="success">Menor lance</Badge> : null}
                            {l.aceitouAlvo ? <Badge tone="info">Aceitou o alvo</Badge> : null}
                            {l.acimaDoAlvo ? <Badge tone="warning">Acima do alvo</Badge> : null}
                          </div>
                          <div className="lf-muted">recebido às {fmtHora(l.criadoEm)}</div>
                        </div>
                        <div className="lf-row-side">
                          <span className="lf-row-valor">{fmtBRL(l.valor)}</span>
                          <span className="lf-row-prazo">{l.prazoDias} d</span>
                          {encerrada ? <span className={`lf-row-check${escolhido ? " is-on" : ""}`}>{escolhido ? "✓ Escolhido" : "Escolher"}</span> : null}
                        </div>
                      </Tag>
                    );
                  })}
                </div>
              )}
              {mostrarHistorico ? (
                <div className="lf-historico">
                  {leilao.lances
                    .slice()
                    .sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm))
                    .map((l) => (
                      <div key={l._id} className={`lf-historico-linha${l.vigente ? "" : " is-superado"}`}>
                        <span>{fmtHora(l.criadoEm)}</span>
                        <span>{l.nome || nomeDo(l.idTransportadora)}</span>
                        <span>{fmtBRL(l.valor)}</span>
                        <span>{l.prazoDias} d</span>
                        <span>{l.vigente ? "vigente" : "superado"}</span>
                      </div>
                    ))}
                </div>
              ) : null}
              {encerrada && foraDoMenor ? (
                <div style={{ marginTop: 12 }}>
                  <div className="lf-aviso lf-aviso-warning">
                    O lance escolhido não é o menor. Justifique (mínimo {JUSTIFICATIVA_MIN} caracteres): prazo, atendimento, histórico...
                  </div>
                  <Input type="textarea" rows={2} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} placeholder="Justificativa" />
                </div>
              ) : null}
            </div>

            {leilao.estado === "deserta" ? (
              <div className="lf-section">
                <div className="lf-section-title">Reabrir com novo alvo</div>
                <div className="lf-inline-acao" style={{ marginTop: 0 }}>
                  <Input inputMode="decimal" value={novoAlvo} onChange={(e) => setNovoAlvo(formatBRLInput(e.target.value))} placeholder="Novo preço-alvo (R$)" style={{ maxWidth: 220 }} />
                  <span className="lf-muted">Mesma carga, mesmas convidadas e mesma janela ({fmtDuracao(leilao.duracaoMin)}); os links são novos.</span>
                </div>
              </div>
            ) : null}

            <div className="lf-section">
              <div className="lf-section-title">Convidadas ({resumo.total})</div>
              <div className="lf-rows">
                {(leilao.convidados || []).map((c) => {
                  const cu = CONVIDADO_UI[c.estado] || CONVIDADO_UI.convidado;
                  return (
                    <div key={c._id} className="lf-row">
                      <div className="lf-row-main">
                        <div className="lf-row-nome">
                          {c.nome}
                          {c.naCarteira ? <Badge tone="info">Carteira</Badge> : <Badge tone="neutral">Fora da carteira</Badge>}
                          {/* PO1-3648: convidada que veio da Rede Everlog (origem 'rede'). */}
                          {ehCandidatoRede(c) ? <Badge tone="warning" title={TITULO_SELO_REDE}>{ROTULO_REDE}</Badge> : null}
                        </div>
                        <div className="lf-muted">
                          {c.email} ·{" "}
                          {c.emailEnviadoEm ? (
                            `convite enviado às ${fmtHora(c.emailEnviadoEm)}`
                          ) : c.emailErro ? (
                            <span className="lf-text-danger">convite falhou: {c.emailErro}</span>
                          ) : (
                            "convite pendente"
                          )}
                          {c.lembreteEm ? " · lembrete enviado" : ""}
                          {c.estado === "recusou" && c.motivoRecusa ? ` · motivo: ${c.motivoRecusa}` : ""}
                        </div>
                      </div>
                      <div className="lf-row-side">
                        <Badge tone={cu.tone}>{cu.label}</Badge>
                        {aberta ? (
                          <button type="button" className="lf-link" onClick={() => handleReenviar(c._id)} disabled={busy}>
                            reenviar
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {acaoErro ? <div className="lf-aviso lf-aviso-danger">{acaoErro}</div> : null}
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {aberta ? (
          confirmar ? (
            <div className="lf-confirma">
              <span>{confirmar === "cancelar" ? "Cancelar o leilão? As convidadas não são avisadas." : "Encerrar agora? A janela fecha para novos lances."}</span>
              <Button size="sm" color={confirmar === "cancelar" ? "danger" : "warning"} onClick={confirmar === "cancelar" ? handleCancelar : handleEncerrar} disabled={busy}>
                {busy ? "..." : "Sim"}
              </Button>
              <Button size="sm" color="secondary" outline onClick={() => setConfirmar(null)} disabled={busy}>
                Não
              </Button>
            </div>
          ) : (
            <div className="lf-confirma">
              <Button size="sm" color="danger" outline onClick={() => setConfirmar("cancelar")} disabled={busy}>
                Cancelar leilão
              </Button>
              <Button size="sm" color="warning" outline onClick={() => setConfirmar("encerrar")} disabled={busy}>
                Encerrar agora
              </Button>
            </div>
          )
        ) : null}
        <Button className="lc-btn-cancel" onClick={onClose} disabled={busy}>
          Fechar
        </Button>
        {encerrada ? (
          <Button color="primary" onClick={handleAdjudicar} disabled={busy || !lanceEscolhido}>
            {busy ? "Gravando..." : "Confirmar vencedora"}
          </Button>
        ) : null}
        {leilao && leilao.estado === "deserta" ? (
          <Button color="primary" onClick={handleReabrir} disabled={busy}>
            {busy ? "Reabrindo..." : "Reabrir com novo alvo"}
          </Button>
        ) : null}
      </ModalFooter>
    </Modal>
  );
}
